import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/alert-dialog/alert-dialog';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import { GET_LIST_PRODUCT_QUERY_KEY } from '@/features/products/api/get-list-product.api';
import { privateProductMutationOptions } from '@/features/products/api/private-product.api';

export interface PrivateProductDialogProps extends BaseDialogProps {
  productId: string;
  productTitle: string;
}

export default function PrivateProductDialog({
  dialog,
  productId,
  productTitle,
}: PrivateProductDialogProps) {
  const { closePrivateProductDialog } = useV2DialogContext();
  const queryClient = useQueryClient();

  const privateProductMutation = useMutation({
    ...privateProductMutationOptions(),
    onSuccess: () => {
      toast.success('Produk berhasil dijadikan privat');
      queryClient.invalidateQueries({ queryKey: [GET_LIST_PRODUCT_QUERY_KEY] });
      closePrivateProductDialog();
    },
    onError: error => {
      const message =
        error instanceof AxiosError
          ? ((error.response?.data as { message?: string })?.message ??
            error.message)
          : 'Gagal menjadikan produk privat';
      toast.error(message);
    },
  });

  const isPending = privateProductMutation.isPending;

  return (
    <AlertDialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState && !isPending) {
          closePrivateProductDialog();
        }
      }}
    >
      <AlertDialogContent showCloseButton={false}>
        <AlertDialogHeader>
          <AlertDialogTitle>Jadikan produk ini privat?</AlertDialogTitle>
          <AlertDialogDescription>
            Produk &quot;{productTitle}&quot; akan dijadikan privat dan hanya
            dapat diakses melalui tautan langsung.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Batal</AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            onClick={() =>
              privateProductMutation.mutate({ product_id: productId })
            }
          >
            {isPending ? 'Memproses...' : 'Jadikan Privat'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
